import type { ArStatementRecipientRolesMode } from '@erp/shared-types';
import type { RecipientRole } from '@/components/common/email-composer/types';

/**
 * Assigns To/Cc roles to a customer's "Customer Statement"-tagged recipients.
 *
 * Why this lives client-side: `resolveStatementTaggedRecipientEmails` returns
 * a flat, ordered list of addresses — the To/Cc split is decided in the
 * compose dialog. Keeping the split here means every entry point that opens
 * the dialog (payment-reminder task, aging report, customer page, AR summary)
 * picks up the org's configured mode for free, without a per-caller flag. The
 * decided roles are then carried to the server explicitly via the send
 * contract's `recipientEmails` (To) + `ccRecipientEmails` (Cc) fields — see
 * `buildStatementRecipientPayload`.
 *
 * Map keys are lower-cased. Downstream consumers (the recipients panel's
 * suggestion pool, the To/Cc chip lists, and the send payload) all compare
 * against a lower-cased view of the address, so the key must already be
 * normalized.
 *
 * Insertion order follows the input order, which is the order the server
 * resolved the tagged contacts in.
 *
 * @param emails Ordered, already-filtered recipient addresses.
 * @param mode `to_all` puts every address on To; `first_to_rest_cc` keeps the
 *   historical behavior of To for the first address and Cc for the rest.
 */
export function assignStatementRecipientRoles(
  emails: string[],
  mode: ArStatementRecipientRolesMode,
): Map<string, RecipientRole> {
  const roles = new Map<string, RecipientRole>();
  emails.forEach((email, index) => {
    const role: RecipientRole = mode === 'to_all' || index === 0 ? 'to' : 'cc';
    roles.set(email.toLowerCase(), role);
  });
  return roles;
}

export interface StatementRecipientSplit {
  toEmails: string[];
  ccEmails: string[];
}

/**
 * List-shaped counterpart of `assignStatementRecipientRoles`, for callers that
 * never materialize a roles map — the Send All fallback applies the org's
 * configured mode to the server-resolved default recipients with this.
 */
export function splitStatementRecipients(
  emails: string[],
  mode: ArStatementRecipientRolesMode,
): StatementRecipientSplit {
  if (mode === 'to_all') {
    return { toEmails: [...emails], ccEmails: [] };
  }
  return { toEmails: emails.slice(0, 1), ccEmails: emails.slice(1) };
}

/**
 * Send-payload fields carrying the To/Cc split to the server.
 *
 * When a To list is present, `ccRecipientEmails` is always included — even
 * empty — because its presence is what tells the server the input is
 * role-separated; omitting it triggers the legacy positional split (first
 * address To, rest Cc), which would demote extra To addresses to Cc.
 *
 * The contract requires at least one To (`recipientEmails.min(1)`), so when
 * the user demoted every address to Cc we fall back to the legacy flat list
 * and let the server promote the first address to To — the pre-roles-setting
 * behavior for that edge case.
 */
export function buildStatementRecipientPayload({
  toEmails,
  ccEmails,
}: StatementRecipientSplit): {
  recipientEmails: string[];
  ccRecipientEmails?: string[];
} {
  if (toEmails.length === 0) {
    return { recipientEmails: ccEmails };
  }
  return { recipientEmails: toEmails, ccRecipientEmails: ccEmails };
}
